import { EdgarContext, EdgarFilingSection } from '../types/geminiTypes';

const SEC_USER_AGENT = 'OracleInsight noreply@oracleinsight.ai';
const secHeaders = {
  'User-Agent': SEC_USER_AGENT,
  'Accept-Encoding': 'gzip, deflate',
};

// In-memory cache for company_tickers.json — refreshed every 24 hours.
// Resets on server restart (acceptable — file changes rarely).
let _tickerCikCache: Record<string, { cik: string; name: string }> | null = null;
let _tickerCikCacheTime = 0;
const TICKER_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function fetchWithTimeout(url: string, timeoutMs: number = 15000): Promise<string> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { headers: secHeaders, signal: controller.signal });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    return await res.text();
  } catch (err: any) {
    console.error(`[EDGAR] fetchWithTimeout failed for ${url}:`, err.message || err);
    throw err;
  } finally {
    clearTimeout(id);
  }
}

function extractSection(text: string, titleRegex: RegExp, nextRegex: RegExp, maxLength: number): string {
  const matches = [...text.matchAll(titleRegex)];
  if (matches.length === 0) return "";
  // Usually the last match or second match is the actual body, the first is ToC.
  // We take the match that is deepest in the document (but before the middle, maybe).
  // Actually, usually the actual section is the latter one.
  const startMatch = matches.length > 1 ? matches[matches.length - 1] : matches[0];
  const startIdx = startMatch.index !== undefined ? startMatch.index : 0;
  
  const textFromStart = text.slice(startIdx);
  const nextMatches = [...textFromStart.matchAll(nextRegex)];
  let endIdx = Math.min(startIdx + maxLength * 2, text.length); // limit search
  if (nextMatches.length > 0) {
     // skip the first match if it's very close (sometimes next item is listed in a header)
     const validNext = nextMatches.find(m => m.index !== undefined && m.index > 100);
     if (validNext && validNext.index) {
        endIdx = startIdx + validNext.index;
     }
  }

  let excerpt = text.slice(startIdx, endIdx);
  if (excerpt.length > maxLength) {
    excerpt = excerpt.slice(0, maxLength) + "...";
  }
  return excerpt.trim();
}

function extractSections10K(text: string, form: string): Omit<EdgarFilingSection, 'filingType' | 'filingDate'> {
  // Item 7 / 1A regex
  // Note: 10-Q MD&A is usually Item 2.
  const mdaRegex = form === "10-K" ? /Item\s+7[^\w]+Management/gi : /Item\s+2[^\w]+Management/gi;
  const mdaNextRegex = form === "10-K" ? /Item\s+7A[^\w]+Quantitative/gi : /Item\s+3[^\w]+Quantitative/gi;
  
  const riskRegex = /Item\s+1A[^\w]+Risk/gi;
  const riskNextRegex = /Item\s+1B[^\w]+Unresolved|Item\s+2[^\w]+Properties/gi;
  
  const segmentRegex = /segment[^\w]+results|reporting[^\w]+segments/gi;
  const segmentNextRegex = /liquidity|financial[^\w]+condition/gi;
  
  const mdaExcerpt = extractSection(text, mdaRegex, mdaNextRegex, 4000);
  const riskFactorsExcerpt = extractSection(text, riskRegex, riskNextRegex, 4000);
  const segmentExcerpt = extractSection(text, segmentRegex, segmentNextRegex, 2000);
  
  return { mdaExcerpt, riskFactorsExcerpt, segmentExcerpt };
}

function extractSections20F(text: string): Omit<EdgarFilingSection, 'filingType' | 'filingDate'> {
  const mdaRegex = /Item\s+5\b/gi;
  const mdaNextRegex = /Item\s+6\b/gi;
  
  const risk3DRegex = /Item\s+3D\b/gi;
  const risk3Regex = /Item\s+3\b/gi;
  const riskNextRegex = /Item\s+4\b/gi;
  
  // Prefer Item 3D over broader Item 3
  let riskFactorsExcerpt = extractSection(text, risk3DRegex, riskNextRegex, 4000);
  if (!riskFactorsExcerpt) {
      riskFactorsExcerpt = extractSection(text, risk3Regex, riskNextRegex, 4000);
  }
  
  const segmentRegex = /Item\s+4\b/gi;
  const segmentNextRegex = /Item\s+4A\b|Item\s+5\b/gi;
  
  const mdaExcerpt = extractSection(text, mdaRegex, mdaNextRegex, 4000);
  const segmentExcerpt = extractSection(text, segmentRegex, segmentNextRegex, 2000);
  
  return { mdaExcerpt, riskFactorsExcerpt, segmentExcerpt };
}

async function resolveCik(ticker: string): Promise<{ cik: string; name: string } | null> {
  const now = Date.now();

  if (!_tickerCikCache || now - _tickerCikCacheTime > TICKER_CACHE_TTL_MS) {
    console.log('[EDGAR] Refreshing company_tickers.json cache...');
    try {
      const raw = await fetchWithTimeout('https://www.sec.gov/files/company_tickers.json');
      const data = JSON.parse(raw);
      // Shape: { "0": { cik_str: 320193, ticker: "AAPL", title: "Apple Inc." }, ... }
      const cache: Record<string, { cik: string; name: string }> = {};
      for (const entry of Object.values(data) as any[]) {
        cache[String(entry.ticker).toUpperCase()] = {
          cik: String(entry.cik_str).padStart(10, '0'),
          name: entry.title,
        };
      }
      _tickerCikCache = cache;
      _tickerCikCacheTime = now;
      console.log(`[EDGAR] company_tickers.json loaded: ${Object.keys(cache).length} tickers`);
    } catch (err: any) {
      console.error('[EDGAR] Failed to load company_tickers.json:', err.message || err);
      return null;
    }
  }

  const entry = _tickerCikCache![ticker.toUpperCase()];
  if (!entry) {
    console.warn(`[EDGAR] Ticker ${ticker.toUpperCase()} not found in company_tickers.json`);
    return null;
  }
  return entry;
}

export async function fetchEdgarContext(ticker: string): Promise<EdgarContext> {
  try {
    console.log(`[EDGAR] Starting fetch for ${ticker}...`);
    await delay(100);

    const resolved = await resolveCik(ticker);
    if (!resolved) {
      console.warn(`[EDGAR] CIK resolution failed for ${ticker} — EDGAR context unavailable`);
      return { ticker, cik: '', companyName: ticker, filings: [], fetchSuccess: false, errorNote: 'CIK not found in company_tickers.json' };
    }

    const { cik: paddedCik, name: companyName } = resolved;
    const rawCik = String(parseInt(paddedCik, 10)); // strip leading zeros for archive URL
    console.log(`[EDGAR] CIK resolved for ${ticker}: ${paddedCik} (${companyName})`);

    await delay(200);
    // 2. Fetch Filing Index
    const subUrl = `https://data.sec.gov/submissions/CIK${paddedCik}.json`;
    console.log(`[EDGAR] Fetching submissions index: ${subUrl}`);
    const subJsonText = await fetchWithTimeout(subUrl, 8000);
    console.log(`[EDGAR] Submissions index received: ${subJsonText.length} bytes`);
    const subData = JSON.parse(subJsonText);
    console.log(`[EDGAR] Submissions parsed. Recent forms count: ${subData.filings?.recent?.form?.length ?? 0}`);
    const forms = subData.filings?.recent?.form || [];
    const accNums = subData.filings?.recent?.accessionNumber || [];
    const primaryDocs = subData.filings?.recent?.primaryDocument || [];
    const filingDates = subData.filings?.recent?.filingDate || [];
    
    const targetFilings: {form: string, date: string, acc: string, doc: string}[] = [];
    let kCount = 0;
    let qCount = 0;
    
    const SUPPORTED_ANNUAL_FORMS = ['10-K', '20-F'];

    for (let i = 0; i < forms.length; i++) {
       const form = forms[i];
       if (!SUPPORTED_ANNUAL_FORMS.includes(form) && form !== "10-Q") {
          continue;
       }
       if (SUPPORTED_ANNUAL_FORMS.includes(form) && kCount < 1) {
          targetFilings.push({ form, date: filingDates[i], acc: accNums[i], doc: primaryDocs[i] });
          kCount++;
       }
       if (form === "10-Q" && qCount < 1) {
          targetFilings.push({ form, date: filingDates[i], acc: accNums[i], doc: primaryDocs[i] });
          qCount++;
       }
       if (kCount >= 1 && qCount >= 1) break;
    }
    
    const results: EdgarFilingSection[] = [];
    
    for (const f of targetFilings) {
       await delay(200);
       const docUrl = `https://www.sec.gov/Archives/edgar/data/${parseInt(rawCik, 10)}/${f.acc.replace(/-/g, '')}/${f.doc}`;
       console.log(`[EDGAR] Fetching document: ${f.form} (${f.date}) — ${docUrl}`);
       try {
           const html = await fetchWithTimeout(docUrl, 30000);
           // clean up HTML
           const cleanText = html.replace(/<[^>]+>/g, ' ')
               .replace(/&nbsp;/gi, ' ')
               .replace(/&#160;/gi, ' ')
               .replace(/\s+/g, ' ');
               
           const filingType = f.form;
           let sections: Omit<EdgarFilingSection, 'filingType' | 'filingDate'>;
           
           if (filingType === '20-F') {
               sections = extractSections20F(cleanText);
           } else {
               sections = extractSections10K(cleanText, filingType);
           }
           
           // If we didn't find segment, just empty string
           results.push({
               filingType: f.form,
               filingDate: f.date,
               mdaExcerpt: sections.mdaExcerpt,
               riskFactorsExcerpt: sections.riskFactorsExcerpt,
               segmentExcerpt: sections.segmentExcerpt
           });
       } catch (err) {
           console.error("Error fetching or parsing document:", docUrl, err);
       }
    }
    
    if (results.length > 0) {
      console.log(`[EDGAR] ✓ ${ticker}: fetched ${results.length} filing(s). Types: ${results.map(f => f.filingType).join(', ')}`);
    } else {
      console.warn(`[EDGAR] ✗ ${ticker}: submissions index found but no filing documents parsed`);
    }

    return {
       ticker,
       cik: paddedCik,
       companyName,
       filings: results,
       fetchSuccess: results.length > 0,
       errorNote: results.length === 0 ? "Failed to parse any filings" : ""
    };
  } catch (err: any) {
    console.error("EDGAR Fetch Error:", err);
    return {
       ticker,
       cik: "",
       companyName: ticker,
       filings: [],
       fetchSuccess: false,
       errorNote: err.message || "Unknown EDGAR error"
    };
  }
}
